// Lector mínimo de archivos Excel (.xlsx) para importar listas de productos: devuelve las filas de la primera hoja como arreglos de celdas
// (texto o número), igual que parseCsv. Un .xlsx es un zip de XML: aquí solo se leen las cadenas compartidas y las celdas de la hoja.
import { strFromU8, unzipSync } from 'fflate';

const parseXml = (text) => new DOMParser().parseFromString(text, 'application/xml');
const kids = (node, name) => Array.from(node.getElementsByTagName(name));

// "B8" → columna 1 (A = 0). Soporta columnas de varias letras ("AA" → 26).
const colIndex = (ref) => {
  const letters = String(ref).match(/^[A-Z]+/i)?.[0].toUpperCase() || 'A';
  return [...letters].reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
};

const sharedStrings = (files) => {
  const raw = files['xl/sharedStrings.xml'];
  if (!raw) return [];
  // Cada <si> puede traer texto simple (<t>) o con formato (varios <r><t>): se une todo el texto.
  return kids(parseXml(strFromU8(raw)), 'si').map((si) => kids(si, 't').map((t) => t.textContent).join(''));
};

// Ruta de la primera hoja según el libro (por si no se llama sheet1.xml).
const firstSheetPath = (files) => {
  try {
    const book = parseXml(strFromU8(files['xl/workbook.xml']));
    const rels = parseXml(strFromU8(files['xl/_rels/workbook.xml.rels']));
    const rid = kids(book, 'sheet')[0]?.getAttribute('r:id');
    const target = kids(rels, 'Relationship').find((r) => r.getAttribute('Id') === rid)?.getAttribute('Target');
    if (target) {
      const path = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
      if (files[path]) return path;
    }
  } catch {
    // se usa la ruta habitual
  }
  return 'xl/worksheets/sheet1.xml';
};

export const readXlsxRows = (buffer) => {
  let files;
  try {
    files = unzipSync(new Uint8Array(buffer));
  } catch {
    throw new Error('El archivo no es un Excel (.xlsx) válido. Si es un .xls antiguo, ábrelo en Excel y guárdalo como .xlsx o CSV.');
  }
  const sheet = files[firstSheetPath(files)];
  if (!sheet) throw new Error('No encontré la hoja de datos dentro del Excel.');
  const strings = sharedStrings(files);
  const rows = [];
  kids(parseXml(strFromU8(sheet)), 'row').forEach((rowEl, i) => {
    const n = Number(rowEl.getAttribute('r')) || i + 1;
    const cells = [];
    kids(rowEl, 'c').forEach((c) => {
      const type = c.getAttribute('t');
      const v = c.getElementsByTagName('v')[0]?.textContent;
      let value = '';
      if (type === 's') value = strings[Number(v)] ?? '';
      else if (type === 'inlineStr') value = kids(c, 't').map((t) => t.textContent).join('');
      else if (type === 'b') value = v === '1' ? 'Sí' : 'No';
      else if (v !== undefined && v !== '') value = type === 'str' || type === 'e' ? v : (Number.isFinite(Number(v)) ? Number(v) : v);
      cells[colIndex(c.getAttribute('r') || 'A')] = value;
    });
    for (let k = 0; k < cells.length; k += 1) if (cells[k] === undefined) cells[k] = '';
    while (rows.length < n - 1) rows.push([]); // las filas vacías conservan su número, como en el archivo
    rows[n - 1] = cells;
  });
  while (rows.length && rows[rows.length - 1].every((c) => String(c ?? '').trim() === '')) rows.pop();
  return rows;
};
